/**
 * GetStartedQuiz — 3-question homepage funnel that replaces the bare
 * email input in the hero. Role → # of units → Province → then hands
 * the visitor off to /signup with the answers pre-filled as URL params
 * so the signup form skips duplicate questions.
 *
 * Kept intentionally small (no LLM, no external calls) — the goal is
 * higher conversion by removing friction, not gathering data.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Home, Building2, Check } from "lucide-react";

const PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland & Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const UNIT_BUCKETS = [
  { key: "1-5",   label: "1–5 units" },
  { key: "6-20",  label: "6–20 units" },
  { key: "21-50", label: "21–50 units" },
  { key: "50+",   label: "50+ units" },
];

export default function GetStartedQuiz() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [role, setRole] = useState(null);
  const [units, setUnits] = useState(null);
  const [province, setProvince] = useState(null);

  function finish(prov) {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (units) params.set("units", units);
    if (prov || province) params.set("province", prov || province);
    nav(`/signup?${params.toString()}`);
  }

  return (
    <div data-testid="get-started-quiz" className="w-full min-w-0 max-w-full overflow-hidden rounded-3xl bg-white border-2 border-emerald-200 shadow-xl p-4 sm:p-6 md:p-8">
      {/* Progress dots */}
      <div className="flex items-center gap-2 mb-6" data-testid="quiz-progress">
        {[0, 1, 2].map((i) => (
          <div key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-emerald-600" : "bg-slate-200"}`}
            data-testid={`quiz-dot-${i}`}
          />
        ))}
      </div>

      {/* STEP 0 — Role */}
      {step === 0 && (
        <div data-testid="quiz-step-role">
          <div className="text-sm uppercase tracking-widest font-bold text-emerald-700">Step 1 of 3</div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
            What brings you here?
          </h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="quiz-role-landlord"
              onClick={() => { setRole("landlord"); setStep(1); }}
              className="group min-w-0 flex flex-col items-start gap-2 p-4 sm:p-5 rounded-2xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 text-left transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <Building2 className="w-6 h-6"/>
              </div>
              <div className="font-bold text-base sm:text-lg text-slate-900">I&apos;m a landlord</div>
              <div className="text-xs sm:text-sm text-slate-600 leading-snug">Collect rent, screen tenants, manage repairs.</div>
            </button>
            <button
              type="button"
              data-testid="quiz-role-tenant"
              onClick={() => {
                // Tenants skip the units + province questions; go straight
                // to signup so we don't waste their time.
                const params = new URLSearchParams({ role: "tenant" });
                nav(`/signup?${params.toString()}`);
              }}
              className="group min-w-0 flex flex-col items-start gap-2 p-4 sm:p-5 rounded-2xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50/50 text-left transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                <Home className="w-6 h-6"/>
              </div>
              <div className="font-bold text-base sm:text-lg text-slate-900">I&apos;m a tenant</div>
              <div className="text-xs sm:text-sm text-slate-600 leading-snug">Pay rent, request repairs, message my landlord.</div>
            </button>
          </div>
        </div>
      )}

      {/* STEP 1 — Units */}
      {step === 1 && (
        <div data-testid="quiz-step-units">
          <button
            type="button"
            onClick={() => setStep(0)}
            data-testid="quiz-back-to-role"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-3"
          >
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
          <div className="text-sm uppercase tracking-widest font-bold text-emerald-700">Step 2 of 3</div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
            How many units do you manage?
          </h3>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {UNIT_BUCKETS.map((b) => (
              <button
                key={b.key}
                type="button"
                data-testid={`quiz-units-${b.key}`}
                onClick={() => { setUnits(b.key); setStep(2); }}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  units === b.key
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50"
                }`}
              >
                <div className="font-bold text-lg text-slate-900">{b.label}</div>
                {units === b.key && (
                  <div className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-700 font-semibold">
                    <Check className="w-4 h-4"/> Picked
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2 — Province */}
      {step === 2 && (
        <div data-testid="quiz-step-province">
          <button
            type="button"
            onClick={() => setStep(1)}
            data-testid="quiz-back-to-units"
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-3"
          >
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
          <div className="text-sm uppercase tracking-widest font-bold text-emerald-700">Step 3 of 3</div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
            Which province are your rentals in?
          </h3>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PROVINCES.map((p) => (
              <button
                key={p.code}
                type="button"
                data-testid={`quiz-province-${p.code.toLowerCase()}`}
                onClick={() => { setProvince(p.code); finish(p.code); }}
                className="px-3 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-slate-800 text-left transition-all"
              >
                <div className="text-sm font-bold text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 font-semibold">{p.code}</div>
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="quiz-skip-province"
            onClick={() => finish("")}
            className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-slate-800"
          >
            Skip and start paid plan <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      )}
    </div>
  );
}
